import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import * as _ from 'lodash';
import { catchError, map, tap } from 'rxjs/operators';

import { LoggingService } from '../../framework/logging/logging.service';
import { AppInjector } from '../../app-injector.service';
import { ErrorUtilitiesService } from '../components/framework/errorhandling/error-utilities.service';

interface IApiResponse<T> {
  message: string;
  result: T;
};

interface IApiOptions {
  params?: HttpParams | {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl: string = "";
  constructor(
    private http: HttpClient,
    private errorUtilities: ErrorUtilitiesService
  ) { }

  private getHeaders(): HttpHeaders {
    const token = this.getToken();
    const headers: any = {
      'Content-Type': 'application/json'
    };

    // Only add Authorization header if we have a valid token
    if (token) {
      headers.Authorization = token;
    }

    // Add timezone information to every request
    this.addTimezoneHeaders(headers);

    return new HttpHeaders(headers);
  }

  private getMultiPartHeaders(): HttpHeaders {
    const token = this.getToken();
    const headers: any = {
      'enctype': 'multipart/form-data'
    };

    // Only add Authorization header if we have a valid token
    if (token) {
      headers.Authorization = token;
    }

    // Add timezone information to every request
    this.addTimezoneHeaders(headers);

    return new HttpHeaders(headers);
  }

  private getToken() {
    return localStorage.getItem('tasksApiToken');
  }

  /**
   * Add timezone information to request headers
   * @param headers - The headers object to add timezone information to
   */
  private addTimezoneHeaders(headers: any): void {
    try {
      // Get the user's timezone
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Get the timezone offset in minutes (positive for UTC+, negative for UTC-)
      const timeZoneOffsetMinutes = -new Date().getTimezoneOffset();

      // Convert offset to hours for easier server-side processing
      const timeZoneOffsetHours = timeZoneOffsetMinutes / 60;

      // Add custom headers for timezone information
      headers['X-Client-Timezone'] = timeZone;
      headers['X-Client-Timezone-Offset'] = timeZoneOffsetMinutes.toString();
      headers['X-Client-Timezone-Offset-Hours'] = timeZoneOffsetHours.toString();

      // Add a formatted timezone offset string (e.g., "+05:00", "-08:00")
      const offsetHours = Math.floor(Math.abs(timeZoneOffsetMinutes) / 60);
      const offsetMinutes = Math.abs(timeZoneOffsetMinutes) % 60;
      const sign = timeZoneOffsetMinutes >= 0 ? '+' : '-';
      const formattedOffset = `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
      headers['X-Client-Timezone-Offset-String'] = formattedOffset;

      // Get locale information
      const userLocale = navigator.language || navigator.languages?.[0] || 'en-US';

      // Add locale headers
      headers['X-Client-Locale'] = userLocale;
      headers['X-Client-Language'] = userLocale.split('-')[0]; // Extract language code (e.g., 'en' from 'en-US')
      headers['X-Client-Country'] = userLocale.split('-')[1] || ''; // Extract country code (e.g., 'US' from 'en-US')

    } catch (error) {
      console.warn('Failed to get timezone information:', error);
      // If timezone detection fails, provide fallback values
      headers['X-Client-Timezone'] = 'UTC';
      headers['X-Client-Timezone-Offset'] = '0';
      headers['X-Client-Timezone-Offset-Hours'] = '0';
      headers['X-Client-Timezone-Offset-String'] = '+00:00';
    }
  }

  public getWithParameters<T>(url: string, params: HttpParams): any {
    let result = this.http
      .get<T>(url, { headers: this.getHeaders(), params: params })
      .pipe(map((data: T) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public get<T>(url: string): any {
    let result = this.http
      .get<T>(url, { headers: this.getHeaders() })
      .pipe(map((data: T) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public fetch<T>(url: string, options?: IApiOptions): Observable<T> {
    const result = this.http
      .get<IApiResponse<T>>(url, { headers: this.getHeaders(), ...options })
      .pipe(
        map(response => response.result),
        catchError(this.handleError)
      );

    return result;
  }

  public post<T>(url: string, body: T): any {
    let result = this.http
      .post(url, body, { headers: this.getHeaders() })
      .pipe(map((data: any) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public postMultiPartFormData<T>(url: string, body: T): any {
    let result = this.http
      .post(url, body, { headers: this.getMultiPartHeaders() })
      .pipe(map((data: any) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public putMultiPartFormData<T>(url: string, body: T): any {
    let result = this.http
      .put<T>(url, body, {
        headers: this.getMultiPartHeaders(),
        observe: 'response',
      })
      .pipe(map((data) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public put<T>(url: string, entities: T[]): any {
    let result = this.http
      .put<T>(url, entities, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(map((data) => _.values(data)))
      .pipe(catchError(this.handleError));
    return result;
  }

  public putSingle<T>(url: string, body: T): any {
    return this.http
      .put<T>(url, body, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(map((data: any) => _.values(data)))
      .pipe(catchError(this.handleError));
  }

  /*
  public uploadFile(url: string, fileToUpload: File): any {
    const token = this.getToken();
    const headers: any = {};

    // Only add Authorization header if we have a valid token
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add timezone information to upload requests
    this.addTimezoneHeaders(headers);

    const httpHeaders = new HttpHeaders(headers);

    const formData: FormData = new FormData();
    formData.append('filekey', fileToUpload, fileToUpload.name);
    const result = this.http
      .post(url, formData, {
        headers: httpHeaders,
        observe: 'response',
      })
      .pipe(tap((data: any) => this.handleError(data)));
    return result;
  }
  */

  public delete(url: string, id: string): any {
    const apiUrl = url + '/' + id;
    let result = this.http
      .delete(apiUrl, {
        headers: this.getHeaders(),
        observe: 'response',
      })
      .pipe(map((data: any) => _.values(data)))
      .pipe(catchError(this.handleError));
    // console.log('delete result: ', result);
    return result;
  }



  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError = (error: any) => {
    // Get LoggingService through the injector to avoid circular dependencies
    const injector = AppInjector.getInstance().getInjector();
    const loggingService = injector.get(LoggingService);

    // Extract user-friendly message using ErrorUtilitiesService
    const userMessage = this.errorUtilities.extractErrorMessage(error);

    // Extract meaningful error information for logging
    const errorMessage = `API call failed: ${error.message || error}`;
    const errorDetails = {
      error: error.error || error,
      status: error.status || 'Unknown',
      statusText: error.statusText || 'Unknown',
      url: error.url || 'Unknown'
    };

    // Log the error with detailed information
    loggingService.logError(`${errorMessage} - Details: ${JSON.stringify(errorDetails)}`);

    // Ensure the error object has a message property with user-friendly text
    if (!error.message) {
      error.message = userMessage;
    }

    // console.log('error thrown: ', error);
    return throwError(() => error);
  }
}
